import { toTitleCase } from "lib";
import { isMobileSafariType } from "./devices";

export enum EContractStatus {
  BUYER_ACCEPTED = "BUYER ACCEPTED",
  COMPLETED = "COMPLETED",
  DRAFTED = "DRAFTED",
  NOT_DRAFTED = "NOT DRAFTED",
  PENDING = "PENDING",
  SENT_TO_BUYER = "SENT TO BUYER",
  SENT_TO_VENDOR = "SENT TO VENDOR",
  SENT = "SENT",
  VENDOR_ACCEPTED = "VENDOR ACCEPTED",
  VOID = "VOID",
}

export const hasBeenSentToBuyer = (eContract?: EContract): boolean => {
  const { buyerAcceptanceEmailIds = [], buyerSharedCount = 0 } =
    eContract ?? {};
  return buyerAcceptanceEmailIds.length > 0 || buyerSharedCount > 0;
};

export const hasBeenSentToVendor = (eContract?: EContract): boolean => {
  const { vendorAcceptanceEmailIds = [], vendorSharedCount = 0 } =
    eContract ?? {};
  return vendorAcceptanceEmailIds.length > 0 || vendorSharedCount > 0;
};

export const getEContractStatus = (eContract?: EContract) => {
  if (!eContract) {
    return EContractStatus.NOT_DRAFTED;
  }
  if (eContract.voidedOn) {
    return EContractStatus.VOID;
  }
  if (eContract.buyerAcceptedOn && eContract.vendorAcceptedOn) {
    return EContractStatus.COMPLETED;
  }
  if (eContract.buyerAcceptedOn) {
    return EContractStatus.BUYER_ACCEPTED;
  }
  if (eContract.vendorAcceptedOn) {
    return EContractStatus.VENDOR_ACCEPTED;
  }
  const hasBuyerBeenSent = hasBeenSentToBuyer(eContract);
  const hasVendorBeenSent = hasBeenSentToVendor(eContract);

  if (hasBuyerBeenSent || hasVendorBeenSent) {
    if (!hasBuyerBeenSent) {
      return EContractStatus.SENT_TO_VENDOR;
    }
    if (!hasVendorBeenSent) {
      return EContractStatus.SENT_TO_BUYER;
    }
    return EContractStatus.SENT;
  }
  if (eContract.agentAcceptedOn) {
    return EContractStatus.DRAFTED;
  }
  return EContractStatus.PENDING;
};

export const getEContractStatusColor = (status: EContractStatus): string => {
  if (status === EContractStatus.VOID) {
    return "errorRed";
  }
  if (status === EContractStatus.COMPLETED) {
    return "success";
  }
  if (
    [
      EContractStatus.BUYER_ACCEPTED,
      EContractStatus.VENDOR_ACCEPTED,
      EContractStatus.SENT_TO_BUYER,
      EContractStatus.SENT_TO_VENDOR,
      EContractStatus.SENT,
      EContractStatus.DRAFTED,
    ].includes(status)
  ) {
    return "inProgress";
  }
  return "gray78";
};

export const buildEContractSmsHref = (link: string) => {
  // iOS's sms: URI scheme expects an (empty) recipient before &body=;
  // Android and others expect ?body= with no recipient segment.
  const separator = isMobileSafariType() ? "&" : "?";
  return `sms:${separator}body=${encodeURIComponent(
    `Please review and accept your eContract: ${link}`,
  )}`;
};

export const getEContractCodeVersionAndStatusText = (eContract: EContract) => {
  if (!eContract) {
    return "--";
  }

  const status = toTitleCase(getEContractStatus(eContract));
  const version = `v${eContract.version}`;

  return `${eContract.code} ${version} - ${status}`;
};
